import { cache } from 'react';
import { format } from 'date-fns';

import type { EnumCustomerType, FulfillmentType, TreezStore } from '@/types';
import { isCurrentDateInRange, toZonedDate } from '@/utils';
import {
  filterSliceItemsByLocationSlug,
  locationSlugMatchesStore,
  showSliceByLocationSlug,
} from '@/utils/locationSlug';

import {
  getSelectedCustomerTypeFromServer,
  getSelectedFulfillmentTypeFromServer,
  getSelectedStoreFromServer,
} from '../server';

// --- Types ---

export type SliceRequestContext = {
  customerType: EnumCustomerType | undefined;
  fulfillmentType: FulfillmentType | undefined;
  selectedStore: TreezStore | undefined;
  storeUrlSlug: string | undefined;
};

export type SliceItemBase = {
  active?: boolean;
  day?: string | null;
  end_date?: string | null;
  location_slug?: string | null;
  start_date?: string | null;
};

export type SlicePrimaryBase = {
  active?: boolean;
  location_slug?: string | null;
};

export type ResolveSliceContentOptions<TItem extends SliceItemBase> = {
  items?: TItem[] | null;
  primary: SlicePrimaryBase;
  storeUrlSlug: string | undefined;
};

export type SliceContentResult<TItem extends SliceItemBase> = {
  alternateItems: TItem[];
  items: TItem[];
};

// --- Migrated slice types (no Suspense in StreamedSliceZone) ---

/** Slice types resolved on the server with store-specific items. */
export const SSR_MIGRATED_SLICE_TYPES = new Set([
  'banner',
  'cta_feature',
  'carousel',
  'hero_image',
  'video',
]);

export const isSsrMigratedSlice = (sliceType: string): boolean =>
  SSR_MIGRATED_SLICE_TYPES.has(sliceType);

// --- Request context ---

export const getSliceRequestContext = cache(
  async (): Promise<SliceRequestContext> => {
    const [
      { selectedStore },
      { selectedCustomerType },
      { selectedFulfillmentType },
    ] = await Promise.all([
      getSelectedStoreFromServer(),
      getSelectedCustomerTypeFromServer(),
      getSelectedFulfillmentTypeFromServer(),
    ]);

    return {
      selectedStore,
      storeUrlSlug: selectedStore?.url_slug,
      customerType: selectedCustomerType,
      fulfillmentType: selectedFulfillmentType,
    };
  },
);

// --- Schedule filter ---

export const filterSliceItemsBySchedule = <T extends SliceItemBase>(
  items: T[] | null | undefined,
  currentDay = format(toZonedDate(), 'eeee'),
): T[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter(
    item =>
      item.active !== false &&
      (!item?.day || item.day === 'Everyday' || item.day === currentDay) &&
      isCurrentDateInRange(
        item?.start_date as string,
        item?.end_date as string,
      ),
  );
};

export const isSliceScheduleVisible = (fields: SliceItemBase): boolean =>
  filterSliceItemsBySchedule([fields]).length > 0;

// --- Slice content resolution ---

export const resolveSliceContent = <TItem extends SliceItemBase>({
  items,
  primary,
  storeUrlSlug,
}: ResolveSliceContentOptions<TItem>): SliceContentResult<TItem> => {
  const emptyResult: SliceContentResult<TItem> = {
    alternateItems: [],
    items: [],
  };

  if (!showSliceByLocationSlug(primary.location_slug as string, storeUrlSlug)) {
    return emptyResult;
  }

  const scheduleFiltered = filterSliceItemsBySchedule(
    (items ?? []).filter(item => item.active !== false),
  );

  const currentItems = filterSliceItemsByLocationSlug(
    scheduleFiltered,
    storeUrlSlug,
  );

  const alternateItems = scheduleFiltered.filter(
    item =>
      Boolean(item.location_slug?.trim()) &&
      !locationSlugMatchesStore(item.location_slug, storeUrlSlug),
  );

  return {
    alternateItems,
    items: currentItems,
  };
};
